import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";
import ReactFlow, {
  Background,
  Controls as FlowControls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  NodeTypes,
  Panel,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import Navbar from "@/components/layout/Navbar";
import { useOrgHierarchy } from "@/hooks/useOrgHierarchy";
import { useOrgChartState } from "@/hooks/useOrgChartState";
import { findNodeByPid, getPathToNode } from "@/lib/orgChartValidation";
import { convertOrgTreeToFlow, getLayoutedElements } from "@/lib/orgChartLayout";
import { OrgChartFlowNode } from "@/components/admin/OrgChartFlowNode";
import { OrgChartControls } from "@/components/admin/OrgChartControls";
import { OrgChartSearch } from "@/components/admin/OrgChartSearch";
import { MemberProfileModal } from "@/components/admin/MemberProfileModal";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { OrgNode } from "@/types";

// Define custom node types for React Flow
const nodeTypes: NodeTypes = {
  orgNode: OrgChartFlowNode,
};

/**
 * Inner component that uses React Flow hooks
 */
function OrgChartFlow() {
  const reactFlowInstance = useReactFlow();
  const { orgTree, totalMembers, departments, loading, error, refetch } = useOrgHierarchy();
  const {
    collapsedNodes,
    focusedNode,
    toggleCollapse,
    setFocusedNode,
    expandNode,
  } = useOrgChartState();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedNode, setSelectedNode] = useState<OrgNode | null>(null);
  const [highlightedNodes, setHighlightedNodes] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(1);
  const [pendingNavigation, setPendingNavigation] = useState<OrgNode | null>(null);

  // Handle node click
  const handleNodeClick = useCallback((node: OrgNode) => {
    setSelectedNode(node);
    setIsModalOpen(true);
    setFocusedNode(node.pid);
  }, [setFocusedNode]);

  // Convert org tree to React Flow nodes and edges
  const { initialNodes, initialEdges } = useMemo(() => {
    if (!orgTree) return { initialNodes: [], initialEdges: [] };
    
    const { nodes, edges } = convertOrgTreeToFlow(orgTree, collapsedNodes);
    const layouted = getLayoutedElements(nodes, edges);
    
    return {
      initialNodes: layouted.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          onToggleCollapse: toggleCollapse,
          onNodeClick: handleNodeClick,
          isHighlighted: node.id === focusedNode,
        },
      })),
      initialEdges: layouted.edges,
    };
  }, [orgTree, collapsedNodes, focusedNode, toggleCollapse, handleNodeClick]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes when data changes
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      toast.success("Org chart refreshed");
    } catch (err) {
      toast.error("Failed to refresh org chart");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle zoom controls
  const handleZoomIn = () => {
    reactFlowInstance.zoomIn();
    setCurrentZoom(reactFlowInstance.getZoom());
  };

  const handleZoomOut = () => {
    reactFlowInstance.zoomOut();
    setCurrentZoom(reactFlowInstance.getZoom());
  };

  const handleResetView = () => {
    reactFlowInstance.fitView({ padding: 0.2 });
    setCurrentZoom(reactFlowInstance.getZoom());
  };

  // Update zoom level when viewport changes
  const handleViewportChange = useCallback(() => {
    setCurrentZoom(reactFlowInstance.getZoom());
  }, [reactFlowInstance]);

  // Handle navigation to member from modal
  const handleNavigateToMember = (memberPid: string) => {
    if (!orgTree) return;
    const node = findNodeByPid(orgTree, memberPid);
    if (node) {
      setSelectedNode(node);
      setFocusedNode(memberPid);
    }
  };

  // Handle search node selection
  const handleSearchNodeSelect = useCallback((node: OrgNode) => {
    if (!orgTree) return;
    
    // Get path from root to target node
    const path = getPathToNode(orgTree, node.pid);
    if (!path) {
      toast.error(`Could not locate ${node.fullName} in the org tree`);
      return;
    }
    
    // Expand all collapsed ancestors (excluding the target node itself)
    const ancestors = path.slice(0, -1);
    let needsExpansion = false;
    
    ancestors.forEach(ancestorPid => {
      if (collapsedNodes.includes(ancestorPid)) {
        expandNode(ancestorPid);
        needsExpansion = true;
      }
    });
    
    // Set focused node
    setFocusedNode(node.pid);
    
    // If we expanded nodes, wait for re-render; otherwise navigate immediately
    if (needsExpansion) {
      setPendingNavigation(node);
    } else {
      // Node is already visible, navigate immediately
      const flowNode = nodes.find(n => n.id === node.pid);
      if (flowNode && flowNode.position) {
        reactFlowInstance.setCenter(
          flowNode.position.x + (flowNode.width || 200) / 2,
          flowNode.position.y + (flowNode.height || 100) / 2,
          { zoom: 1, duration: 800 }
        );
        toast.success(`Navigated to ${node.fullName}`);
      }
    }
  }, [orgTree, nodes, collapsedNodes, reactFlowInstance, setFocusedNode, expandNode]);

  // Handle pending navigation after nodes are expanded
  useEffect(() => {
    if (!pendingNavigation || nodes.length === 0) return;
    const flowNode = nodes.find(n => n.id === pendingNavigation.pid);
    const target = pendingNavigation;
    // Always clear pendingNavigation after attempting — avoids infinite loop if node never found
    const timer = setTimeout(() => {
      if (flowNode && flowNode.position) {
        reactFlowInstance.setCenter(
          flowNode.position.x + (flowNode.width || 200) / 2,
          flowNode.position.y + (flowNode.height || 100) / 2,
          { zoom: 1, duration: 800 }
        );
        toast.success(`Navigated to ${target.fullName}`);
      }
      setPendingNavigation(null);
    }, 100);
    return () => clearTimeout(timer);
  }, [pendingNavigation, nodes, reactFlowInstance]);

  // Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsModalOpen(false);
        setSelectedNode(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading org chart...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex h-[calc(100vh-4rem)] items-center justify-center p-4">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to Load Org Chart</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Empty state
  if (!orgTree) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex h-[calc(100vh-4rem)] items-center justify-center p-4">
          <Alert className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No Organizational Structure</AlertTitle>
            <AlertDescription>
              No organizational hierarchy has been defined yet. Contact your
              administrator to set up the org chart.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="relative h-[calc(100vh-4rem)] overflow-hidden bg-muted/10">
        {/* Search */}
        <OrgChartSearch
          orgTree={orgTree}
          departments={departments}
          onNodeSelect={handleSearchNodeSelect}
          onHighlightNodes={setHighlightedNodes}
        />

        {/* React Flow Org Chart */}
        <div className="absolute inset-0">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onMove={handleViewportChange}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.3}
          maxZoom={1.5}
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: false,
          }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#aaa" gap={16} />
          <FlowControls />
          <MiniMap
            nodeColor={() => 'hsl(var(--primary))'}
            maskColor="rgba(0, 0, 0, 0.1)"
            position="bottom-left"
            className="!bg-background !border-2"
          />
          
          {/* Custom Controls Panel */}
          <Panel position="bottom-right" className="bg-transparent">
            <OrgChartControls
              zoom={currentZoom}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onResetView={handleResetView}
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
            />
          </Panel>
        </ReactFlow>
      </div>

        {/* Member Profile Modal */}
        <MemberProfileModal
          member={selectedNode}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onNavigateToMember={handleNavigateToMember}
        />
      </div>
    </div>
  );
}

/**
 * Main Organizational Chart Page with React Flow Provider
 */
export default function OrgChart() {
  return (
    <ReactFlowProvider>
      <OrgChartFlow />
    </ReactFlowProvider>
  );
}
